import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Base } from "../../framework/common/base";
import AddIcon from "@mui/icons-material/Add";
import { AppUtils } from "../../models/common/appUtils";
import { Link } from "react-router-dom";
import { StartIconButton } from "../framework/startIconButton";
import { optionWorkTimeBreakButtonInMobileEnabled } from "../../models/common/consts";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";

interface IDailyWorkLogTitleProps {
    title: string;
    minutes: number;
    date?: string;
    showZeroDuration?: boolean;
    showNewEntryButton?: boolean;
    showAddBreakButton?: boolean;
    hasWorkShiftTimeSlots?: boolean;
}

export const DailyWorklogTitle = ({
    title,
    minutes,
    date,
    showZeroDuration = false,
    showNewEntryButton,
    showAddBreakButton,
    hasWorkShiftTimeSlots,
}: IDailyWorkLogTitleProps) => {
    const { t } = useTranslation();
    const [dateString, setDateString] = useState<string | null>(null);
    const totalHours = useMemo(
        () => AppUtils.getDurationStrByDurationMin(minutes, showZeroDuration),
        [minutes]
    );

    const [workTimeBreakButtonInMobileEnabled] = useAppOptionValueEquals(
        optionWorkTimeBreakButtonInMobileEnabled,
        true
    );

    const displayBreakButton = showAddBreakButton &&
        workTimeBreakButtonInMobileEnabled &&
        hasWorkShiftTimeSlots;

    // If date parameter is not provided, use current date instead.
    useEffect(() => {
        if (date) {
            setDateString(date);
        } else {
            setDateString(Base.dateToDateJSON(new Date()));
        }
    }, [date]);

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 4,
                }}
            >
                <Typography variant="h4" sx={{ color: "primary.dark", mr: 1 }}>
                    {!!totalHours ? (
                        <>{title}:</>
                    ) : (
                        <>{title}</>
                    )}
                </Typography>

                {!!totalHours ? (
                    <Typography variant="h5" sx={{ color: "success.main", mr: 2 }}>
                        {totalHours}
                    </Typography>
                ) : null}
                {!displayBreakButton && showNewEntryButton && dateString && (
                    <Box ml="auto">
                        <Link to={`/work/form/new/${Base.dateToDateJSON(dateString)}`}>
                            <StartIconButton
                                variant="contained"
                                color="success"
                                startIcon={<AddIcon sx={{ mb: "3px" }} />}
                            >
                                {t("workTime.newEntry")}
                            </StartIconButton>
                        </Link>
                    </Box>
                )}
            </Box>
            {displayBreakButton && (
            <>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    {showNewEntryButton && dateString && (
                        <Link to={`/work/form/new/${Base.dateToDateJSON(dateString)}`}>
                            <StartIconButton
                                variant="contained"
                                color="success"
                                startIcon={<AddIcon sx={{ mb: "3px" }} />}
                            >
                                {t("workTime.newEntry")}
                            </StartIconButton>
                        </Link>
                    )}
                    {dateString && (
                        <Link to={`/work/break/${Base.dateToDateJSON(dateString)}`}>
                            <StartIconButton
                                variant="contained"
                                color="info"
                                startIcon={<AddIcon sx={{ mb: "3px" }} />}
                            >
                                {t("workTime.addBreak")}
                            </StartIconButton>
                        </Link>
                    )}
                </Box>
            </>
            )}
        </>
    );
};
